import { useLazyQuery }                                                           from '@apollo/client'
import { ArrowUturnLeftIcon }                                                     from '@heroicons/react/24/solid'
import { debounce }                                                               from 'lodash-es'
import { useEffect, useState }                                                    from 'react'
import { useDispatch, useSelector }                                               from 'react-redux'
import { useNavigate, useSearchParams }                                           from 'react-router-dom'
import { Button }                                                                 from '../../../components'
import { LoadingEllipsis }                                                        from '../../../components/Loaders'
import SearchBar                                                                  from '../../../components/Search/SearchBar'
import SettingsShopProductsListTable                                              from '../../../components/Settings/ShopSettings/SettingsShopProductsListTable'
import { setShopCategories, setShopProductsList, setShopSettingsProductsRefetch } from '../../../features/shop/shopSlice'
import { GET_SHOP_CATEGORIES, GET_SHOP_PRODUCTS_LIST }                            from '../../../graphql/shop'
import MainLayout                                                                 from '../../../layouts/MainLayout'

export default function SettingsShopProductsList(): JSX.Element {
  const navigate: any                         = useNavigate()
  const dispatch: any                         = useDispatch()
  const [ searchParams ]                      = useSearchParams()
  const currentUser: any                      = useSelector( ( state: any ): any => state.currentUser.user )
  const shopCategories: any                   = useSelector( ( state: any ): any => state.shop.categories )
  const shopProducts: any                     = useSelector( ( state: any ): any => state.shop.products )
  const shopSettingsProductsRefetch: any      = useSelector( ( state: any ): any => state.shop.settingsProductsRefetch )
  const pagination: any                       = useSelector( ( state: any ): any => state.shop.pagination )
  const [ shopCategoryId, setShopCategoryId ] = useState( '' )
  const [ goesBack, setGoesBack ]             = useState( false )

  const [ getShopProductsList, { loading } ] = useLazyQuery( GET_SHOP_PRODUCTS_LIST, {
    fetchPolicy:     shopSettingsProductsRefetch ? 'network-only' : 'cache-first',
    nextFetchPolicy: shopSettingsProductsRefetch ? 'network-only' : 'cache-first',
    onCompleted:     ( { shopProductsList }: any ): void => {
      dispatch( setShopSettingsProductsRefetch( false ) )
      dispatch( setShopProductsList( shopProductsList ) )
    }
  } )

  const [ getShopCategories, { loading: loadingCategories } ] = useLazyQuery( GET_SHOP_CATEGORIES, {
    onCompleted: ( { shopCategories }: any ): void => dispatch( setShopCategories( shopCategories ) )
  } )

  const fetchShopProductsList: any = async ( categoryId: string, page: number ): Promise<void> => {
    await getShopProductsList( {
                                 variables: {
                                   input: {
                                     category_id: categoryId ?? null,
                                     page:        ( page > 0 ) ? page : 1
                                   }
                                 }
                               } )
  }

  const fetchShopCategories: any = async (): Promise<void> => {
    await getShopCategories()
  }

  const handleCategoryChange: any = ( catId: string ): void => {
    if ( !catId ) {
      navigate( '/settings/shop/products' )
    } else if ( shopCategoryId !== catId ) {
      navigate( `/settings/shop/products?category=${ catId }` )
    }
  }

  const [ searchProducts, { data: searchData, loading: searchingProducts } ] = useLazyQuery( GET_SHOP_PRODUCTS_LIST )

  const getSearchProducts: any = async ( query: string = '', page: number = 1 ): Promise<void> => {
    await searchProducts( { variables: { input: { query, page, perPage: 15 } } } )
  }

  const debouncedSearch: any = debounce( async ( query: string, page: number ): Promise<any> => await getSearchProducts( query, page ), 2000 )

  const handleSearch: any = ( value: string ): void => {
    if ( !goesBack && value.length && value.trim().length >= 2 ) {
      debouncedSearch( value, 1 )
    }
  }

  const goBack: any = (): void => {
    navigate( -1 )
  }

  const handleGoBack: any = (): void => {
    setGoesBack( true )
    goBack()
  }

  useEffect( (): any => {
    let shouldFetch: boolean = true

    if ( shouldFetch && !shopCategories?.length ) {
      fetchShopCategories()
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ shopCategories ] )

  useEffect( (): any => {
    const categoryParam: string = searchParams.get( 'category' )
    const pageParam: number     = !isNaN( parseInt( searchParams.get( 'page' ) ) ) ? parseInt( searchParams.get( 'page' ) ) : 1

    if ( categoryParam ) {
      setShopCategoryId( categoryParam )
    } else {
      setShopCategoryId( '' )
      if ( searchParams.has( 'category' ) ) {
        searchParams.delete( 'category' )
      }
    }

    fetchShopProductsList( categoryParam, pageParam )
  }, [ shopProducts, searchParams ] )

  return (
    <MainLayout>
      <div className="max-w-screen-3xl mx-auto w-full">
        <div className="flex-grow lg:p-8 p-3 space-y-4 lg:space-y-8">
          { !currentUser && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4 mt-4 w-full" /> }
          { currentUser?.admin &&
            <>
              <div className="flex justify-between w-full my-4">
                <div className="flex">
                  <Button
                    label="Back"
                    blue
                    onClick={ handleGoBack }
                    icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
                  />
                </div>
                <div className="flex space-x-4">
                  <Button label="Add new product" yellow onClick={ (): any => navigate( '/settings/shop/products/add' ) } />
                </div>
              </div>

              { ( loading || loadingCategories ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

              { !loading && !loadingCategories &&
                <>
                  <div className="row flex items-center w-full bg-white rounded-3xl p-1.5 2xl:py-2 2xl:px-4 mb-6 text-blue-input">
                    <SearchBar
                      resultsInModal
                      goesBack={ goesBack }
                      goBack={ goBack }
                      loading={ searchingProducts }
                      results={ searchData?.shopProductsList?.data ?? null }
                      pagination={ searchData?.shopProductsList?.links ?? null }
                      onChange={ handleSearch }
                      setPage={ getSearchProducts }
                      placeholder="Search products"
                      type="products_settings"
                    />
                  </div>

                  <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 2xl:p-4">
                    <div className="col flex flex-col w-full">
                      <SettingsShopProductsListTable
                        shopProducts={ shopProducts }
                        pagination={ pagination }
                        categoryId={ shopCategoryId }
                        shopCategories={ shopCategories }
                        handleCategoryChange={ handleCategoryChange }
                      />
                    </div>
                  </div>
                </>
              }
            </>
          }
          { !currentUser?.admin &&
            <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 xl:p-4">
              Access Denied!
            </div>
          }
        </div>
      </div>
    </MainLayout>
  )
}
